import React from 'react';

const guides = [
  { id: 'getting-started', title: 'Getting Started' },
  { id: 'view-sensor-data', title: 'How to View Sensor Data' },
  { id: 'add-sensor', title: 'How to Add a Sensor' },
  { id: 'manage-thresholds', title: 'How to View or Edit Thresholds' },
  { id: 'acknowledge-alarm', title: 'How to Acknowledge an Alarm' },
  { id: 'manage-users', title: 'How to Add or Remove a User' },
  { id: 'manage-zones', title: 'How to View, Add, or Remove Zones' },
  { id: 'edit-alarm-notes', title: 'How to Edit Notes on an Alarm' },
  { id: 'troubleshooting', title: 'Troubleshooting & FAQ' },
];

export default function HelpPage() {
  return (
    <div className="p-8 max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">Knowledge Base & Guides</h1>
      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Welcome to the TS1 Dashboard Knowledge Base. Here you'll find step-by-step guides for common tasks and troubleshooting tips.
      </p>
      <nav className="mb-8">
        <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Table of Contents</h2>
        <ul className="list-disc pl-6 space-y-1 text-blue-700 dark:text-blue-400">
          {guides.map(g => (
            <li key={g.id}>
              <a href={`#${g.id}`}>{g.title}</a>
            </li>
          ))}
        </ul>
      </nav>

      {/* Getting Started */}
      <section id="getting-started" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">Getting Started</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Log in with your company credentials.</li>
          <li>Use the sidebar to navigate between Dashboard, Sensors, Alarms, and more.</li>
          <li>Click on a section to view details and perform actions.</li>
        </ol>
      </section>

      {/* View Sensor Data */}
      <section id="view-sensor-data" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to View Sensor Data</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to <b>Sensors</b> from the sidebar.</li>
          <li>Browse the list or use search to find a specific sensor.</li>
          <li>Click a sensor's name to view its detailed data and charts.</li>
          <li>Review live readings, historical charts, and alarm status.</li>
        </ol>
      </section>

      {/* Add Sensor */}
      <section id="add-sensor" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to Add a Sensor</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to <b>Sensors</b> from the sidebar.</li>
          <li>Click the <b>Add Sensor</b> button (usually at the top or above the sensor list).</li>
          <li>Fill in the required sensor details, such as name, serial number, type, and any other necessary information.</li>
          <li>Click <b>Save</b> or <b>Add</b> to register the new sensor.</li>
          <li>The new sensor will appear in the list and can be assigned to zones or configured further.</li>
        </ol>
      </section>

      {/* Manage Thresholds */}
      <section id="manage-thresholds" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to View or Edit Thresholds</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to the <b>Thresholds</b> page from the sidebar.</li>
          <li>Find the sensor or channel whose thresholds (alarm setpoints) you want to view or edit.</li>
          <li>Click the <b>Edit</b> or <b>Set Thresholds</b> button next to the relevant sensor/channel.</li>
          <li>Adjust the high and low alarm values as needed.</li>
          <li>Click <b>Save</b> to apply your changes. The new thresholds will take effect immediately.</li>
        </ol>
      </section>

      {/* Acknowledge Alarm */}
      <section id="acknowledge-alarm" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to Acknowledge an Alarm</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to <b>Alarm Log</b> from the sidebar.</li>
          <li>Find the alarm event you want to acknowledge (unacknowledged alarms are highlighted).</li>
          <li>Click the <b>Acknowledge</b> button in the Actions column.</li>
          <li>The alarm will be marked as acknowledged and the UI will update instantly.</li>
        </ol>
      </section>

      {/* Manage Users */}
      <section id="manage-users" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to Add or Remove a User</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to <b>Users</b> from the sidebar.</li>
          <li>To add a user, click <b>Add User</b> and fill in the required details.</li>
          <li>To remove a user, click the <b>Delete</b> or <b>Remove</b> button next to their name.</li>
          <li>Changes take effect immediately for all users.</li>
        </ol>
      </section>

      {/* Manage Zones */}
      <section id="manage-zones" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to View, Add, or Remove Zones</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to <b>Zones</b> from the sidebar.</li>
          <li>To view details, click on a zone's name in the list.</li>
          <li>To add a new zone, click <b>Add Zone</b> and fill in the required information.</li>
          <li>To remove a zone, click the <b>Delete</b> or <b>Remove</b> button next to the zone you wish to delete.</li>
          <li>Assign sensors or users to a zone using the available options in the zone details view.</li>
        </ol>
      </section>

      {/* Edit Alarm Notes */}
      <section id="edit-alarm-notes" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">How to Edit Notes on an Alarm</h2>
        <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Go to <b>Alarm Log</b> from the sidebar.</li>
          <li>Find the alarm event you want to add a note to.</li>
          <li>Click <b>Edit</b> next to the Notes field.</li>
          <li>Enter your note and click <b>Save Note</b>. Your username will be appended to the note.</li>
        </ol>
      </section>

      {/* Troubleshooting & FAQ */}
      <section id="troubleshooting" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">Troubleshooting & FAQ</h2>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
          <li><b>Can't log in?</b> Double-check your credentials and internet connection. If the problem persists, contact <a href="mailto:support@thermal-systems.co.uk" className="underline text-blue-700 dark:text-blue-400">support</a>.</li>
          <li><b>Data not updating?</b> Try refreshing the page. If the issue continues, check your network or contact support.</li>
          <li><b>Need more help?</b> Visit <a href="https://thermal-systems.co.uk/support" target="_blank" rel="noopener noreferrer" className="underline text-blue-700 dark:text-blue-400">our documentation</a> or email <a href="mailto:support@thermal-systems.co.uk" className="underline text-blue-700 dark:text-blue-400">support@thermal-systems.co.uk</a>.</li>
        </ul>
      </section>
    </div>
  );
} 